// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, { CartLineContainerStyles } from "@tps/App/Components/Cart/CartLineContainer";
import DistributeOrderModal from "@tps/App/Components/Product/DistributeOrderModal";
import { ProductPriceStyles } from "@tps/Overrides/Components/ProductPrice";
import { mergeWithCss } from "@tps/XDL/Utilities";
import React, { FC, useContext, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Change
    showDistributeOrderButton?: boolean;
    showQtyLabel?: boolean;
    showAvailableQty?: boolean;
    editable?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: LinkPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    // XNG-Change
    QtyRemoveGridItem?: InjectableCss;
    qtyLabel?: TypographyPresentationProps;
    totalInnerWrapper?: InjectableCss;
    attributesWrapper?: InjectableCss;
    extendedUnitNetPriceHeadingAndText?: SmallHeadingAndTextStyles;
    webDiscountMessage?: TypographyPresentationProps;
    variantDisplay?: TypographyPresentationProps;
    approximateWeightDisplay?: TypographyPresentationProps;
    distributeOrderButton?: ButtonPresentationProps;
    cartLineContainerStyles?: CartLineContainerStyles;
    branchQtyAvailableHidden?: HiddenProps;
    quantityWrapper?: InjectableCss;
    discountValue?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: [8, 8, 8, 9, 9] },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: { width: 6 },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 20px;
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
        `,
        width: [2, 2, 2, 1, 1],
    },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showQtyLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    showDistributeOrderButton = false,
    showAvailableQty = false,
    editable = true,
}) => {
    const [styles] = React.useState(() => mergeWithCss(cartLineCardExpandedStyles, extendedStyles));
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const webOrderDiscount = useAppSelector(
        state => (getSettingsCollection(state).orderCustomSettings as any)?.webOrderDiscount || "",
    );
    const [isOpen, setIsOpen] = useState(false);

    // Xng-Change
    const [showLineNote, setShowLineNote] = useState(showLineNotes);
    const lineNotesClickHandler = () => {
        setShowLineNote(!showLineNote);
    };

    const handleDistributeOrderClick = () => {
        setIsOpen(true);
    };

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                if (
                    !cart.cartLines?.some(
                        line =>
                            line.id !== cartLine.id && line.properties.warehouseId === cartLine.properties.warehouseId,
                    )
                ) {
                    // this means its the only cartLine in the warehouse, so we need to remove the warehouse shipping info
                    removeCartLine({ cartLineId: cartLine.id, warehouseId: cartLine.properties.warehouseId } as any);
                } else {
                    removeCartLine({ cartLineId: cartLine.id });
                }
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                        properties: {
                            approximateWeight: cartLine.properties?.approximateWeight,
                            totalExtendedUnitNetPriceDiscount: cartLine.properties?.totalExtendedUnitNetPriceDiscount,
                            varientValue: cartLine.properties?.varientValue,
                            warehouse: cartLine.properties?.warehouse,
                            warehouseId: cartLine.properties?.warehouseId,
                        },
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                    properties: {
                        approximateWeight: cartLine.properties?.approximateWeight,
                        totalExtendedUnitNetPriceDiscount: cartLine.properties?.totalExtendedUnitNetPriceDiscount,
                        varientValue: cartLine.properties?.varientValue,
                        warehouse: cartLine.properties?.warehouse,
                        warehouseId: cartLine.properties?.warehouseId,
                    },
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        approximateWeight: cartLine.properties?.approximateWeight,
                        totalExtendedUnitNetPriceDiscount: cartLine.properties?.totalExtendedUnitNetPriceDiscount,
                        varientValue: cartLine.properties?.varientValue,
                        warehouse: cartLine.properties?.warehouse,
                        warehouseId: cartLine.properties?.warehouseId,
                    },
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        if (
            !cart.cartLines?.some(
                line => line.id !== cartLine.id && line.properties.warehouseId === cartLine.properties.warehouseId,
            )
        ) {
            // this means its the only cartLine in the warehouse, so we need to remove the warehouse shipping info
            removeCartLine({ cartLineId: cartLine.id, warehouseId: cartLine.properties.warehouseId } as any);
            return;
        }
        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    // XNG-Changes
    const heading = showLineNote
        ? "Hide Line Notes"
        : cartLine.notes.length === 0
        ? "Add Line Notes"
        : "Show Line Notes";

    return (
        <CartLineContainer
            extendedStyles={styles.cartLineContainerStyles}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <CartLineContainer.Image>
                {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {showBrand && cartLine.brand && (
                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                )}
                {showProductTitle && (
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                )}
                <StyledWrapper {...styles.attributesWrapper}>
                    <Typography {...styles.variantDisplay}>{cartLine?.properties?.varientValue}</Typography>
                    <Typography {...styles.approximateWeightDisplay}>
                        {translate("Approximate Weight:")}&nbsp;{cartLine?.properties?.approximateWeight}&nbsp;
                        {siteMessage("Custom_Weight_Unit")}.
                    </Typography>
                </StyledWrapper>
                {/* {showAvailableQty && (
                    <Hidden {...styles.branchQtyAvailableHidden} above="md">
                        {cartLine.properties?.qtyOnCurrentBrach && (
                            <Typography
                                color={
                                    (cartLine?.qtyOrdered || 0) > Number(cartLine.properties?.qtyOnCurrentBrach)
                                        ? "danger"
                                        : "text"
                                }
                                weight={
                                    (cartLine?.qtyOrdered || 0) > Number(cartLine.properties?.qtyOnCurrentBrach)
                                        ? "bold"
                                        : "normal"
                                }
                                size={12}
                            >{`${cartLine.properties?.qtyOnCurrentBrach} ${translate(
                                "In Stock (This Branch)",
                            )}`}</Typography>
                        )}
                        {cartLine.properties?.qtyOnAllBranches && (
                            <Typography color="text" size={12}>{`${cartLine.properties?.qtyOnAllBranches} ${translate(
                                "In Stock (All Locations)",
                            )}`}</Typography>
                        )}
                    </Hidden>
                 )} */}
                {/* <ProductPartNumbers
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        manufacturerItem={cartLine.manufacturerItem}
                        extendedStyles={styles.productPartNumbers}
                        showProductNumber={showProductNumber}
                    /> */}
                {showVmiBinDetails && currentMode === "Vmi" && (
                    <StyledWrapper {...styles.binInformationsGridItem}>
                        <ProductBinNumbers
                            locationId={cart.vmiLocationId}
                            binId={cartLine.vmiBinId}
                            isCondensed={false}
                            extendedStyles={styles.productBinNumbers}
                        />
                    </StyledWrapper>
                )}
                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                    <StyledWrapper {...styles.configurationGridItem}>
                        {cartLine.sectionOptions!.map(option => (
                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                {`${option.sectionName}:${option.optionName}`}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
                {showPricing &&
                    promotions.map(promotion => (
                        <Typography {...styles.promotionNameText} key={promotion.id}>
                            {promotion.name}
                        </Typography>
                    ))}
                {/* {showInventoryAvailability && !cartLine.quoteRequired && (
                        <ProductAvailability
                            productId={cartLine.productId!}
                            availability={cartLine.availability!}
                            unitOfMeasure={cartLine.unitOfMeasure}
                            trackInventory={cartLine.trackInventory}
                            failedToLoadInventory={cart.failedToGetRealTimeInventory}
                            extendedStyles={styles.productAvailability}
                        />
                    )} */}
                {cart.showCostCode && !cartLine.isPromotionItem && (
                    <StyledWrapper>
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                        {cart.canEditCostCode ? (
                            <Select
                                value={cartLine.costCode}
                                onChange={costCodeChangeHandler}
                                {...styles.costCodeSelect}
                            >
                                {cart.costCodes?.map(costCode => (
                                    <option key={costCode.costCode} value={costCode.costCode}>
                                        {costCode.description}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        )}
                    </StyledWrapper>
                )}
                {cartLine.isSubscription && cartLine.productSubscription && (
                    <ProductDeliveryScheduleButton
                        subscription={
                            cartLine.properties.productSubscription
                                ? JSON.parse(cartLine.properties.productSubscription)
                                : cartLine.productSubscription
                        }
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            !cartLine.qtyOrdered ||
                            cartLine.qtyOrdered <= 0
                        }
                        onSave={saveScheduleHandler}
                        extendedStyles={styles.deliveryScheduleButton}
                    />
                )}
                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Typography
                                {...styles.cartLineErrorMessageText}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                data-test-selector="cartline_errorMessage"
                            >
                                {message}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        currencySymbol={cart.currencySymbol}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        showInvalidPriceMessage={true}
                        extendedStyles={styles.productPrice}
                        showLabel={showPriceLabel}
                        showUnitOfMeasure={showPerUnitPrice}
                    />
                )}
                {/* XNG-Changes */}                
                {webOrderDiscount && (
                    <Typography {...styles.webDiscountMessage}>
                        {`Web Order (${Number(webOrderDiscount) * 100}%) `}
                    </Typography>
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <StyledWrapper {...styles.quantityWrapper}>
                    {showQtyLabel && <Typography {...styles.qtyLabel}>{translate("QTY")}</Typography>}
                    <StyledWrapper {...styles.QtyRemoveGridItem}>
                        <CartLineQuantity
                            cart={cart}
                            editable={editable}
                            onQtyOrderedChange={qtyOrderedChangeHandler}
                            extendedStyles={styles.quantity}
                        />
                        {showRemoveAction && (
                            <Link
                                onClick={removeCartLineClickHandler}
                                data-test-selector="cartline_removeLine"
                                aria-label="Remove item"
                                {...styles.removeCartLineIcon}
                            />
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                {showDistributeOrderButton &&
                    (cartLine.qtyOrdered || 0) > Number(cartLine.properties?.qtyOnCurrentBrach) && (
                        <>
                            <Button onClick={handleDistributeOrderClick} {...styles.distributeOrderButton}>
                                {translate("Distribute Order")}
                            </Button>
                            {isOpen && (
                                <DistributeOrderModal
                                    isOpen={isOpen}
                                    setIsOpen={setIsOpen}
                                    productContext={{
                                        productInfo: {
                                            productId: cartLine.productId || "",
                                            productDetailPath: cartLine.productUri,
                                            qtyOrdered: cartLine.qtyOrdered || 0,
                                            unitOfMeasure: cartLine.unitOfMeasure,
                                            pricing: cartLine.pricing || undefined,
                                        },
                                        product: {
                                            id: cartLine.productId || "",
                                            productTitle: cartLine.productName || cartLine.shortDescription,
                                            properties: { warehouseId: cartLine.properties.warehouseId },
                                        },
                                    }}
                                />
                            )}
                        </>
                    )}
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                <StyledWrapper {...styles.totalInnerWrapper}>
                    {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                        <StyledWrapper>
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPriceHeadingAndText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text={
                                    enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay
                                }
                            />
                            {enableVat && (
                                <>
                                    <Typography as="p" {...styles.vatLabelText}>
                                        {vatPriceDisplay === "DisplayWithVat" ||
                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                            ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                            : translate("Ex. VAT")}
                                    </Typography>
                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <>
                                            <Typography {...styles.subtotalWithoutVatText}>
                                                {cartLine.pricing.extendedUnitNetPriceDisplay}
                                            </Typography>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {translate("Ex. VAT")}
                                            </Typography>
                                        </>
                                    )}
                                </>
                            )}
                            {/* XNG-Changes */}                            
                            {/* <StyledWrapper>
                                {webOrderDiscount && (
                                    <Typography {...styles.webDiscountMessage}>
                                        {`Web Order (${Number(webOrderDiscount) * 100}%) `}
                                        {cartLine.properties?.totalExtendedUnitNetPriceDiscount && (
                                            <Typography {...styles.discountValue} size={12} color="success">
                                                {` -${cartLine.properties?.totalExtendedUnitNetPriceDiscount}`}
                                            </Typography>
                                        )}
                                    </Typography>
                                )}
                                {showAvailableQty && (
                                    <Hidden {...styles.branchQtyAvailableHidden} below="lg">
                                        {!!cartLine.properties?.qtyOnCurrentBrach && (
                                            <Typography
                                                size={12}
                                                color={
                                                    (cartLine?.qtyOrdered || 0) >
                                                    Number(cartLine.properties?.qtyOnCurrentBrach)
                                                        ? "danger"
                                                        : "text"
                                                }
                                                weight={
                                                    (cartLine?.qtyOrdered || 0) >
                                                    Number(cartLine.properties?.qtyOnCurrentBrach)
                                                        ? "bold"
                                                        : "normal"
                                                }
                                            >{`${cartLine.properties?.qtyOnCurrentBrach} ${translate(
                                                "In Stock (This Branch)",
                                            )}`}</Typography>
                                        )}
                                        {!!cartLine.properties?.qtyOnAllBranches && (
                                            <Typography color="text" size={12}>{`${
                                                cartLine.properties?.qtyOnAllBranches
                                            } ${translate("In Stock (All Locations)")}`}</Typography>
                                        )}
                                    </Hidden>
                                )}
                            </StyledWrapper> */}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </CartLineContainer.Total>
            <CartLineContainer.Actions>
                {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                    <CheckboxGroup>
                        <Checkbox
                            checked={showLineNote}
                            onChange={lineNotesClickHandler}
                            data-test-selector="cartline_notesShowHideButton"
                            css={css`
                                white-space: nowrap;
                            `}
                        >
                            {translate(heading)}
                            <VisuallyHidden>{translate(heading)}</VisuallyHidden>
                        </Checkbox>
                    </CheckboxGroup>
                )}
                {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler} aria-label="Add to List">
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.Actions>

            <CartLineContainer.Notes>
                {showLineNote && (
                    <CartLineNotes
                        cart={cart}
                        editable={true}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
